import React, { useCallback, useEffect, useRef } from 'react';
import { sandboxBridge } from './sandboxBridge';

// How long to coalesce back-to-back preview reloads (WEB-7787). Small enough
// that an explicit Save/reload still feels instant, large enough to swallow a
// burst of reloadKey bumps that arrive within the same interaction.
const RELOAD_DEBOUNCE_MS = 300;

export interface PageIframeInit {
	userId: number | null;
	clientId: number | null;
	orderId: number | null;
	language: string | null;
	currentRoute: string | null;
}

interface Props {
	slug: string;
	init: PageIframeInit;
	className?: string;
	style?: React.CSSProperties;
	title?: string;
	/**
	 * When true, the iframe loads the page's current (possibly unpublished)
	 * compiled bundle from `/api/v2/agentPage/:slug/draft.js` instead of the
	 * public `/bundle.js`. Used by the WEB-6723 authoring shell. Defaults to
	 * false so public-URL viewers keep seeing the active bundle.
	 */
	draft?: boolean;
	/**
	 * Bumping this forces the iframe to reload — used by the authoring shell
	 * to re-fetch the compiled bundle after a save without unmounting the
	 * sandbox bridge.
	 */
	reloadKey?: number;
	onCrash?: (msg: { message: string; stack?: string; componentStack?: string }) => void;
}

/**
 * Renders a sandboxed iframe pointed at the Pages shell.
 *
 * Phase 1 (this slice) runs with `sandbox="allow-scripts allow-same-origin"`
 * so the shell can authenticate fetches against the Upsales API with the
 * user's session cookie. Per the project doc this is the "internal-MVP
 * relaxation" — Pages are authored by Upsales staff, so the lighter
 * sandbox is acceptable.
 *
 * Phase 2 (WEB-6726) moves this to a true cross-origin subdomain so the
 * iframe gets an unguessable opaque origin, with per-page CSP nonces and
 * a session-token RPC bridge instead of cookie-credentialed fetches.
 *
 * Even with `allow-same-origin`, the iframe still:
 *   - Cannot navigate the top frame at all (no allow-top-navigation). Pages
 *     are fully sealed mini-apps — internal "views" are state, not URL
 *     changes. Anchor clicks and `window.location = …` from inside a Page
 *     are blocked.
 *   - Cannot submit forms (no allow-forms).
 *   - Cannot open popups (no allow-popups).
 *   - Can only escalate to the parent via the postMessage RPC bridge.
 *
 * The parent posts an `init` message after the iframe loads, and re-posts
 * whenever `init` changes so the page can react to user/route updates.
 */
export default function PageIframe({ slug, init, className, style, title, draft, reloadKey, onCrash }: Props) {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const loadedRef = useRef(false);
	// `reloadKey` is NOT in the src: changing the src navigates the iframe (white
	// flash). Instead we post a `reload` message so the shell re-fetches + re-
	// renders the bundle in place — no flash (WEB-7757).
	const src = `/sandboxed-pages/${encodeURIComponent(slug)}${draft ? '?draft=1' : ''}`;

	const postInit = useCallback(() => {
		const win = iframeRef.current?.contentWindow;
		if (!win) return;
		win.postMessage({ type: 'init', ...init }, '*');
	}, [init]);

	const onLoad = useCallback(() => {
		loadedRef.current = true;
		// Register this iframe with the RPC bridge as soon as it loads. The
		// bridge listens for postMessages from registered iframes and forwards
		// SDK tool calls (tools.crmMutation, tools.readCRM, etc.) to the
		// `/api/v2/agentPage/rpc` endpoint. WEB-6721.
		sandboxBridge.register(iframeRef.current?.contentWindow ?? null, slug);
		postInit();
	}, [postInit, slug]);

	useEffect(() => {
		// Unregister on unmount or slug change. Re-registration happens on
		// the next onLoad. We capture the window inside the effect so the
		// cleanup function unregisters the *same* iframe we registered.
		const win = iframeRef.current?.contentWindow;
		return () => {
			sandboxBridge.unregister(win ?? null);
		};
	}, [slug]);

	useEffect(() => {
		if (loadedRef.current) postInit();
	}, [postInit]);

	// Ask the shell to re-fetch + re-render the bundle in place when the parent
	// bumps reloadKey (e.g. after a build). Skipped on first mount / before load
	// (the initial fetch happens on the shell's own load).
	//
	// Debounced (WEB-7787): a reload re-evaluates the bundle and re-runs the page
	// component from scratch, re-firing every readCRM the page makes on mount. If
	// reloadKey bumps several times in quick succession (autosave landing while a
	// chat turn also applies code, rapid saves, etc.) we only want the last one to
	// hit — each new bump cancels the pending reload and schedules a fresh one, so
	// a burst collapses into a single recompile.
	useEffect(() => {
		if (!loadedRef.current || reloadKey === undefined) return;
		const id = window.setTimeout(() => {
			iframeRef.current?.contentWindow?.postMessage({ type: 'reload', r: reloadKey }, '*');
		}, RELOAD_DEBOUNCE_MS);
		return () => window.clearTimeout(id);
	}, [reloadKey]);

	useEffect(() => {
		if (!onCrash) return;
		const handler = (event: MessageEvent) => {
			if (event.source !== iframeRef.current?.contentWindow) return;
			const data = event.data;
			if (!data || typeof data !== 'object' || data.type !== 'page.crash') return;
			onCrash({ message: data.message, stack: data.stack, componentStack: data.componentStack });
		};
		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, [onCrash]);

	return (
		<iframe
			ref={iframeRef}
			src={src}
			sandbox="allow-scripts allow-same-origin"
			onLoad={onLoad}
			className={className}
			title={title || `Upsales Page: ${slug}`}
			style={{ border: 0, width: '100%', height: '100%', display: 'block', ...style }}
		/>
	);
}
